import { useEffect, useState } from 'react'
import { trackEvent } from '@/utils/analytics'
import { Upload, Loader2 } from 'lucide-react'
import { v4 as uuidv4 } from 'uuid'
import { toast } from 'sonner'
import { useTranslation } from '@/i18n'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import Modal from '@/components/Modal'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { useAppStore } from '@/store'
import {
  ConnectorEnum,
  ConnectorEnumType,
  ConnectorMode,
  defaultConnectorRecord,
  connectorConfigSchema,
  effectiveConnectorSchema,
  visibleConnectorTypes,
  eventhouseDisabledReason,
  displayProvisionStatus,
  getEventhouseProvisionState,
} from '@/types/connector'
import KeyValueEditor, {
  fromRecordToEntry,
  fromEntryToRecord,
  validateEntries,
  buildUpdateConfig,
} from '@/components/KeyValueEditor'

type ConnectorEditModalProps = {
  isOpen: boolean
  onClose: () => void
}


const ConnectorEditModal = ({ isOpen, onClose }: ConnectorEditModalProps) => {
  const { t } = useTranslation()
  const {
    connectorEditData,
    createConnector,
    updateConnector,
    setConnectorEditData,
    user,
    eventhouseAvailable,
    probeEventhouseAvailability,
    connectors,
  } = useAppStore()

  // One Eventhouse per tenant (server enforces with a 409). Editing the
  // existing one must still offer its own type.
  const existingEventhouse = connectors.find((c) => c.type === ConnectorEnum.EVENTHOUSE)
  const alreadyHasEventhouse =
    Boolean(existingEventhouse) && connectorEditData.type !== ConnectorEnum.EVENTHOUSE
  // Shown ON the disabled option rather than removing it — see
  // eventhouseDisabledReason. The failed case gets its own wording because a
  // failed provision still holds the slot, which is the confusing one.
  const eventhouseBlocked = eventhouseDisabledReason(
    alreadyHasEventhouse,
    existingEventhouse
      ? displayProvisionStatus(getEventhouseProvisionState(existingEventhouse))
      : null
  )

  // The EventHouse type is flag-gated server-side; probe once when the modal
  // opens so the option only shows for enabled tenants (403 = hidden).
  useEffect(() => {
    if (isOpen) void probeEventhouseAvailability()
  }, [isOpen, probeEventhouseAvailability])

  // PI read lane (batch | live). Lives in config jsonb; drives which config fields show.
  const mode =
    (connectorEditData.config?.mode as string) || ConnectorMode.BATCH

  // Local state to handle configuration editing
  const [configEntries, setConfigEntries] = useState(
    fromRecordToEntry({}, connectorConfigSchema[ConnectorEnum.GENERIC])
  )

  // Submit state is LOCAL to this modal — deliberately NOT the store's global
  // `loading`. The /connectors page polls `fetchConnectors()` every 5s, which flips
  // that global flag; binding the Create/Update button to it made the button flicker
  // into a disabled spinner every ~5s ("keeps reloading") and swallowed any click that
  // landed while a poll was in-flight. This tracks only the in-progress save.
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Initializes configEntries when connectorEditData.config changes (config carries
  // `mode`, so switching mode re-derives the visible field set via effectiveConnectorSchema).
  useEffect(() => {
    if (connectorEditData.config) {
      const m =
        (connectorEditData.config?.mode as string) || ConnectorMode.BATCH
      setConfigEntries(
        fromRecordToEntry(
          connectorEditData.config,
          effectiveConnectorSchema(connectorEditData.type, m)
        )
      )
    } else {
      setConfigEntries([])
    }
  }, [connectorEditData.config, connectorEditData.type])

  const resetConnectorConfigData = () => {
    setConnectorEditData(defaultConnectorRecord())
    setConfigEntries([])
  }

  // If connector type changes, all the config should be reseted to its new config type
  const handleTypeChange = (newType: ConnectorEnumType) => {
    setConnectorEditData({ type: newType })
    setConfigEntries(
      fromRecordToEntry({}, effectiveConnectorSchema(newType, ConnectorMode.BATCH))
    )
  }

  // Switching read lane rewrites config.mode; the useEffect above re-derives the fields.
  const handleModeChange = (newMode: string) => {
    setConnectorEditData({
      config: { ...connectorEditData.config, mode: newMode },
    })
  }

  // On submit, validate all entries and only if there are no errors, build the global object
  const handleSubmit = async () => {
    const validated = validateEntries(configEntries, t)
    setConfigEntries(validated)
    const errors = validated.filter((entry) => entry.error)
    if (errors.length > 0) {
      toast.error(t('connectors:configuration_errors'))
      return
    }
    const config = fromEntryToRecord(validated)
    setIsSubmitting(true)
    try {
      if (connectorEditData.id === '') {
        const creationTime = new Date().toISOString()
        const newConnector = {
          ...connectorEditData,
          id: uuidv4(),
          // Preserve config keys that aren't editor fields (e.g. `mode`, set by the Mode
          // selector) — fromEntryToRecord only carries schema fields, so build the same
          // way as the update path or `mode` is silently dropped on create.
          config: buildUpdateConfig(
            connectorEditData.config,
            config,
            effectiveConnectorSchema(connectorEditData.type, mode)
          ),
          config_timestamp: creationTime,
          created_by: user?.userId || '',
          created_at: creationTime,
          tenant_id: '', // Will be set by backend from JWT
          last_connection: new Date(0).toISOString(),
          active: false,
        }
        await createConnector(newConnector)
        trackEvent('connector-created', { connector_type: connectorEditData.type, connector_id: newConnector.id })
      } else {
        // The PUT replaces the config jsonb, so preserve connector-managed keys the
        // form hides (e.g. the auto-pinned document_id) while letting edited form
        // fields win — see buildUpdateConfig. Generic connectors stay form-authoritative.
        await updateConnector(connectorEditData.id, {
          name: connectorEditData.name,
          description: connectorEditData.description,
          interval_connection: connectorEditData.interval_connection,
          config: buildUpdateConfig(
            connectorEditData.config,
            config,
            effectiveConnectorSchema(connectorEditData.type, mode)
          ),
        })
        trackEvent('connector-edited', { connector_id: connectorEditData.id, connector_type: connectorEditData.type })
      }
      onClose()
      resetConnectorConfigData()
    } catch (error) {
      if (error instanceof Error) {
        toast.error(`${t('connectors:error_processing_connector')}: ${error.message}`)
      } else {
        toast.error(t('connectors:error_processing_connector'))
      }
      console.error(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const onCloseReset = () => {
    onClose()
    resetConnectorConfigData()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCloseReset}
      title={
        connectorEditData.id === ''
          ? t('connectors:create_new_connector')
          : t('connectors:edit_connector')
      }
      className="w-full max-w-lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCloseReset}>
            {t('common:buttons.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !connectorEditData.name}
          >
            {isSubmitting ? (
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            ) : (
              <Upload className="w-5 h-5 mr-2" />
            )}
            {connectorEditData.id === '' ? t('common:buttons.create') : t('common:buttons.update')}
          </Button>
        </div>
      }
    >
      <div className="grid gap-4 py-4">
        {/* Basic fields */}
        <div className="grid gap-2">
          <Label htmlFor="name">{t('forms:labels.connector_name')}</Label>
          <Input
            id="name"
            value={connectorEditData.name}
            onChange={(e) => setConnectorEditData({ name: e.target.value })}
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="description">{t('forms:labels.description')}</Label>
          <Textarea
            id="description"
            value={connectorEditData.description}
            onChange={(e) =>
              setConnectorEditData({ description: e.target.value })
            }
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="type">{t('forms:labels.connector_type')}</Label>
          <Select
            value={connectorEditData.type}
            onValueChange={(value) =>
              handleTypeChange(value as ConnectorEnumType)
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue defaultValue={'generic'} />
            </SelectTrigger>
            <SelectContent>
              {visibleConnectorTypes(eventhouseAvailable).map((value) => {
                const blocked = value === ConnectorEnum.EVENTHOUSE ? eventhouseBlocked : null
                return (
                  <SelectItem key={value} value={value} disabled={Boolean(blocked)}>
                    {value.toUpperCase()}
                    {blocked && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        {t(`connectors:provision.${blocked}`, {
                          defaultValue:
                            blocked === 'limit_failed'
                              ? '— one per tenant; retry or delete the failed one'
                              : '— one per tenant',
                        })}
                      </span>
                    )}
                  </SelectItem>
                )
              })}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-2">
          <Label htmlFor="interval_connection">{t('forms:labels.interval')}</Label>
          <Input
            id="interval_connection"
            type="number"
            value={connectorEditData.interval_connection}
            onChange={(e) =>
              setConnectorEditData({
                interval_connection: Number(e.target.value),
              })
            }
          />
        </div>
        {/* PI read-lane selector — drives which config fields apply (batch vs live) */}
        {connectorEditData.type === ConnectorEnum.PI && (
          <div className="grid gap-2">
            <Label htmlFor="mode">{t('connectors:mode.label')}</Label>
            <Select value={mode} onValueChange={handleModeChange}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ConnectorMode.BATCH}>
                  {t('connectors:mode.batch')}
                </SelectItem>
                <SelectItem value={ConnectorMode.LIVE}>
                  {t('connectors:mode.live')}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        {/* AF Discovery is NOT here — it needs a live connector, so it lives as a
            per-row "Discover" action (ConnectorDataTable) enabled once the connector
            is Online. The create/edit modal only sets static config. */}
        {/* Section to edit the configuration (mode-aware field set for PI) */}
        <div className="grid gap-2">
          <Label>{t('forms:labels.configuration')}</Label>
          <KeyValueEditor
            entries={configEntries}
            onChange={setConfigEntries}
            schema={effectiveConnectorSchema(connectorEditData.type, mode)}
          />
        </div>
      </div>
    </Modal>
  )
}

export default ConnectorEditModal
